import type { ChildTextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { setReactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { TrackChangeType } from 'models/changes'
import { UnitsOfMeasureKey } from 'models/projectSettings'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback } from 'react'
import { v4 as newGuid } from 'uuid'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { focusPartialUOMIfNeeded } from './useUOMSave'

const readIsBothUnitsMode = (store: ReturnType<typeof useWorkspace2Store.getState>) =>
    store.projectSettings?.unitOfMeasure?.toString().toUpperCase() === UnitsOfMeasureKey.BOTH

const findSOInNodes = (nodes: Partial<ContentItemViewNodesType>[], soId: string) =>
    nodes
        .flatMap((n) => n.elements ?? [])
        .filter((og) => og.type === NODE_TYPES.OptionGroup)
        .flatMap((og) => og.elements ?? [])
        .find((so) => so.id === soId)

const splitTextAroundCursor = (getFocusedElState?: () => { value: string; cursorPos: number }) => {
    const state = getFocusedElState?.()
    const fullText = state?.value ?? ''
    const cursorPos = state?.cursorPos ?? fullText.length
    return { before: fullText.slice(0, cursorPos), after: fullText.slice(cursorPos) }
}

const makeTextElement = (text: string, authoredUserId: string) => ({
    id: newGuid(),
    type: NODE_TYPES.Text,
    text,
    elements: [],
    imperialUnits: '',
    metricUnits: '',
    changeType: TrackChangeType.ADDED,
    placeholderText: '',
    authoredUserId,
})

const makeUOMElement = (authoredUserId: string) => ({
    id: newGuid(),
    type: 'UnitsOfMeasure',
    text: '',
    elements: [],
    imperialUnits: '',
    metricUnits: '',
    changeType: TrackChangeType.ADDED,
    placeholderText: '',
    authoredUserId,
})

const applyUOMInsertionToNodes = (
    nodes: Partial<ContentItemViewNodesType>[],
    soId: string,
    focusedElId: string,
    before: string,
    after: string,
    authoredUserId: string
): Partial<ContentItemViewNodesType>[] =>
    nodes.map((node) => ({
        ...node,
        elements: (node.elements || []).map((og) => {
            if (og.type !== NODE_TYPES.OptionGroup) return og
            return {
                ...og,
                elements: (og.elements || []).map((so) => {
                    if (so.id !== soId) return so
                    const soEls: Partial<ChildTextContentElement>[] = so.elements || []
                    const idx = soEls.findIndex((e) => e.id === focusedElId)
                    if (idx === -1) return so
                    return {
                        ...so,
                        elements: [
                            ...soEls.slice(0, idx),
                            makeTextElement(before, authoredUserId),
                            makeUOMElement(authoredUserId),
                            makeTextElement(after, authoredUserId),
                            ...soEls.slice(idx + 1),
                        ],
                    }
                }),
            }
        }),
    })) as Partial<ContentItemViewNodesType>[]

export const useRegisterUOMHandlerForPillTextFocus = (contentItemId: string | undefined) =>
    useCallback(
        (focusedElId: string, getFocusedElState?: () => { value: string; cursorPos: number }) => {
            setReactiveWorkspaceActions({
                isInsertUOM: true,
                omWorkspaceInsertUOMHandler: () => {
                    if (!contentItemId) return
                    const store = useWorkspace2Store.getState()
                    const isBothMode = readIsBothUnitsMode(store)
                    const soElements = findSOInNodes(store.contentItemViewNodes, contentItemId)?.elements ?? []
                    if (focusPartialUOMIfNeeded(contentItemId, soElements, isBothMode)) return
                    const authoredUserId = userInfo()?.currentUserInfo?.kpUserId ?? ''
                    const { before, after } = splitTextAroundCursor(getFocusedElState)
                    const newNodes = applyUOMInsertionToNodes(
                        store.contentItemViewNodes,
                        contentItemId,
                        focusedElId,
                        before,
                        after,
                        authoredUserId
                    )
                    store.actions.setContentItemViewNodes(newNodes)
                    setReactiveWorkspaceActions({ isInsertUOM: false, omWorkspaceInsertUOMHandler: undefined })
                },
            })
        },
        [contentItemId]
    )
